import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Employee {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  birth_date: string | null;
  hire_date: string | null;
  termination_date: string | null;
  role: string;
  department: string | null;
  account_code: string | null;
  business_unit: 'us' | 'br' | null;
  base_salary: number;
  salary_currency: string;
  cost_type: 'fixed' | 'variable' | 'mixed';
  has_commission: boolean;
  commission_type: string | null;
  commission_percentage: number | null;
  commission_base: string | null;
  has_bonus: boolean;
  bonus_type: string | null;
  bonus_target: number | null;
  bonus_amount: number | null;
  benefits_cost: number;
  status: string;
  app_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Salary frequency fields
  salary_frequency: 'monthly' | 'biweekly' | 'weekly' | null;
  weekly_payment_type: 'fixed' | 'hourly' | null;
  hourly_rate: number | null;
  projected_hours_per_week: number | null;
  // Payment schedule fields
  payment_schedule_type: 'fixed_day' | 'weekday' | 'biweekly_days' | null;
  payment_day_of_month: number | null;
  payment_day_of_week: number | null;
  payment_biweekly_days: number[] | null;
}

export interface EmployeeAllocation {
  id: string;
  employee_id: string;
  business_unit: 'us' | 'br';
  percentage: number;
  valid_from: string;
  valid_to: string | null;
}

export interface CreateEmployeeData {
  full_name: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  hire_date?: string;
  role: string;
  department?: string;
  account_code?: string;
  business_unit?: 'us' | 'br' | null;
  base_salary: number;
  salary_currency?: string;
  cost_type?: 'fixed' | 'variable' | 'mixed';
  has_commission?: boolean;
  commission_type?: string;
  commission_percentage?: number;
  commission_base?: string;
  has_bonus?: boolean;
  bonus_type?: string;
  bonus_target?: number;
  bonus_amount?: number;
  benefits_cost?: number;
  // Salary frequency fields
  salary_frequency?: 'monthly' | 'biweekly' | 'weekly';
  weekly_payment_type?: 'fixed' | 'hourly';
  hourly_rate?: number;
  projected_hours_per_week?: number;
  // Payment schedule fields
  payment_schedule_type?: 'fixed_day' | 'weekday' | 'biweekly_days';
  payment_day_of_month?: number;
  payment_day_of_week?: number;
  payment_biweekly_days?: number[];
}

export function useEmployees() {
  return useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('full_name');

      if (error) throw error;
      return data as Employee[];
    },
  });
}

export function useActiveEmployees() {
  return useQuery({
    queryKey: ['employees', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;
      return data as Employee[];
    },
  });
}

export function useEmployeesByBusinessUnit(businessUnit: 'us' | 'br' | 'all' = 'all') {
  return useQuery({
    queryKey: ['employees', 'business_unit', businessUnit],
    queryFn: async () => {
      let query = supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('full_name');

      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Employee[];
    },
  });
}

export function useEmployeeAllocations(employeeId: string) {
  return useQuery({
    queryKey: ['employee-allocations', employeeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_bu_allocation')
        .select('*')
        .eq('employee_id', employeeId)
        .order('valid_from', { ascending: false });

      if (error) throw error;
      return data as EmployeeAllocation[];
    },
    enabled: !!employeeId,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEmployeeData) => {
      const { data: result, error } = await supabase
        .from('employees')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result as Employee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Employee> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('employees')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as Employee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('employees')
        .update({ status: 'inactive', termination_date: new Date().toISOString().split('T')[0] })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useEmployeeSummary() {
  return useQuery({
    queryKey: ['employees', 'summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('base_salary, salary_currency, cost_type, benefits_cost, business_unit')
        .eq('status', 'active');

      if (error) throw error;

      const employees = data || [];
      
      const totalUSD = employees
        .filter(e => e.salary_currency === 'USD')
        .reduce((sum, e) => sum + (e.base_salary || 0) + (e.benefits_cost || 0), 0);
      
      const totalBRL = employees
        .filter(e => e.salary_currency === 'BRL')
        .reduce((sum, e) => sum + (e.base_salary || 0) + (e.benefits_cost || 0), 0);
      
      const fixed = employees.filter(e => e.cost_type === 'fixed').length;
      const variable = employees.filter(e => e.cost_type === 'variable').length;
      const mixed = employees.filter(e => e.cost_type === 'mixed').length;

      // Business unit breakdown
      const byBusinessUnit = {
        us: employees.filter(e => e.business_unit === 'us').length,
        br: employees.filter(e => e.business_unit === 'br').length,
        shared: employees.filter(e => e.business_unit === null).length,
      };
      
      return {
        count: employees.length,
        totalUSD,
        totalBRL,
        costTypeBreakdown: { fixed, variable, mixed },
        fixedPercentage: employees.length > 0 ? (fixed / employees.length) * 100 : 0,
        byBusinessUnit,
      };
    },
  });
}

// Helper to get birthdays this month
export function useBirthdaysThisMonth() {
  return useQuery({
    queryKey: ['employees', 'birthdays'],
    queryFn: async () => {
      const currentMonth = new Date().getMonth() + 1;
      
      const { data, error } = await supabase
        .from('employees')
        .select('id, full_name, birth_date')
        .eq('status', 'active')
        .not('birth_date', 'is', null);

      if (error) throw error;

      return (data || [])
        .filter(e => {
          if (!e.birth_date) return false;
          const birthMonth = new Date(e.birth_date).getMonth() + 1;
          return birthMonth === currentMonth;
        })
        .map(e => ({
          id: e.id,
          name: e.full_name,
          day: new Date(e.birth_date!).getDate(),
        }))
        .sort((a, b) => a.day - b.day);
    },
  });
}

// Get employee by app_user_id
export function useEmployeeByAppUserId(appUserId: string | null) {
  return useQuery({
    queryKey: ['employee', 'by-app-user', appUserId],
    queryFn: async () => {
      if (!appUserId) return null;
      
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('app_user_id', appUserId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      return data as Employee | null;
    },
    enabled: !!appUserId,
  });
}

// Get all app_user_ids that are already linked to employees
export function useLinkedAppUserIds() {
  return useQuery({
    queryKey: ['employees', 'linked-app-user-ids'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('app_user_id')
        .eq('status', 'active')
        .not('app_user_id', 'is', null);

      if (error) throw error;
      return (data || []).map(e => e.app_user_id).filter(Boolean) as string[];
    },
  });
}
